import Alpine from "alpinejs";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(CustomEase);

const AKADORI_CONTENT_CERTIFIED_IMAGE_POP_EASE = CustomEase.create(
  "akadoriContentCertifiedImagePopEase",
  "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
);

const setAkadoriCultureFrame = (
  frames: HTMLElement[],
  activeFrame: HTMLElement
) => {
  gsap.set(frames, { opacity: 0 });
  gsap.set(activeFrame, { opacity: 1 });
};

const startAkadoriCultureLoop = (frames: HTMLElement[]) => {
  const culture2 = frames[1];
  const culture1 = frames[2];
  if (!culture1 || !culture2) return;

  const loopTl = gsap.timeline({ repeat: -1 });
  loopTl
    .call(() => setAkadoriCultureFrame(frames, culture1))
    .to({}, { duration: 1 })
    .call(() => setAkadoriCultureFrame(frames, culture2))
    .to({}, { duration: 1 });
};

const addClippedImageAnimation = (
  timeline: gsap.core.Timeline,
  imageFrame: HTMLElement,
  innerImage: HTMLElement,
  position: string | number
) => {
  timeline
    .to(
      imageFrame,
      {
        opacity: 1,
        scale: 1,
        duration: 1.1,
        ease: "back.out(1.4)",
        onComplete: () => {
          gsap.set(imageFrame, { willChange: "auto" });
        },
      },
      position
    )
    .to(
      innerImage,
      {
        scale: 1,
        duration: 2.0,
        ease: "power3.out",
        onComplete: () => {
          gsap.set(innerImage, { willChange: "auto" });
        },
      },
      position
    );
};

export const initAkadoriContent = () => {
  Alpine.data("akadoriContent", () => {
    return {
      init() {
        const root = this.$el as HTMLElement;
        const yamagaImage = this.$refs.yamagaImage as HTMLElement | undefined;
        const graphSection = root.querySelector<HTMLElement>(
          "[data-akadori-graph-section]"
        );
        const graphContainer = root.querySelector<HTMLElement>(
          "[data-akadori-graph]"
        );
        const graphCircle = root.querySelector<SVGElement>(
          "[data-akadori-graph-circle]"
        );
        const graphCircleFill = root.querySelector<SVGPathElement>(
          "[data-akadori-graph-circle-fill]"
        );
        const graphCircleStroke = root.querySelector<SVGPathElement>(
          "[data-akadori-graph-circle-stroke]"
        );
        const graphCircleText = root.querySelector<SVGGElement>(
          "[data-akadori-graph-circle-text]"
        );
        const certifiedImages = Array.from(
          root.querySelectorAll<HTMLElement>("[data-akadori-certified-image]")
        );
        const cultureImage = root.querySelector<HTMLElement>(
          "[data-akadori-culture-image]"
        );
        const cultureFrames = cultureImage
          ? Array.from(
              cultureImage.querySelectorAll<HTMLElement>(
                "[data-akadori-culture-frame]"
              )
            )
          : [];
        const clippedImageFrames = Array.from(
          root.querySelectorAll<HTMLElement>("[data-akadori-content-image]")
        );
        const storySection = root.querySelector<HTMLElement>(
          "[data-akadori-story]"
        );
        const iitokodoriImage = root.querySelector<HTMLElement>(
          "[data-akadori-iitokodori]"
        );
        const clippedImageGroups = Array.from(
          root.querySelectorAll<HTMLElement>(
            "[data-akadori-content-image-group]"
          )
        );
        const clippedInnerImages = clippedImageFrames
          .map((imageFrame) =>
            imageFrame.querySelector<HTMLElement>(
              "[data-akadori-content-image-inner]"
            )
          )
          .filter((image): image is HTMLElement => image instanceof HTMLElement);
        const certifiedInnerImages = certifiedImages
          .map((certifiedImage) =>
            certifiedImage.querySelector<SVGImageElement>(
              "[data-akadori-certified-image-inner]"
            )
          )
          .filter(
            (image): image is SVGImageElement => image instanceof SVGImageElement
          );
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        if (
          !yamagaImage &&
          !graphSection &&
          certifiedImages.length === 0 &&
          !cultureImage &&
          clippedImageFrames.length === 0 &&
          !storySection &&
          !iitokodoriImage
        ) {
          return;
        }

        if (yamagaImage) {
          gsap.set(yamagaImage, {
            opacity: 0,
            yPercent: 5,
            transformOrigin: "right bottom",
          });
        }
        if (graphSection) {
          gsap.set(graphSection, { opacity: 0, yPercent: 10 });
        }
        if (graphCircleStroke) {
          const strokeLen = graphCircleStroke.getTotalLength();
          gsap.set(graphCircleStroke, {
            strokeDasharray: strokeLen,
            strokeDashoffset: -strokeLen,
          });
        }
        if (graphCircleFill) {
          gsap.set(graphCircleFill, { fillOpacity: 0, stroke: "none" });
        }
        if (graphCircleText) {
          gsap.set(graphCircleText, { opacity: 0 });
        }
        gsap.set(certifiedImages, {
          opacity: 0,
          scale: 0.82,
          transformOrigin: "50% 50%",
        });
        certifiedInnerImages.forEach((innerImage) => {
          const origin = innerImage.getAttribute("data-akadori-certified-origin") || "306 269.5";
          gsap.set(innerImage, {
            scale: 1.18,
            svgOrigin: origin,
          });
        });
        gsap.set(clippedImageFrames, {
          opacity: 0,
          scale: 0.9,
          transformOrigin: "50% 50%",
          force3D: true,
          willChange: "transform, opacity",
        });
        gsap.set(clippedInnerImages, {
          scale: 1.05,
          transformOrigin: "center center",
        });
        if (cultureImage) {
          gsap.set(cultureImage, { opacity: 0 });
        }
        if (storySection) {
          gsap.set(storySection, { opacity: 0, yPercent: 10 });
        }
        if (iitokodoriImage) {
          gsap.set(iitokodoriImage, { opacity: 0 });
        }
        if (cultureFrames.length > 0) {
          setAkadoriCultureFrame(cultureFrames, cultureFrames[2] || cultureFrames[0]);
        }

        if (prefersReducedMotion) {
          if (yamagaImage) {
            gsap.set(yamagaImage, {
              opacity: 1,
              yPercent: 0,
            });
          }
          if (graphSection) {
            gsap.set(graphSection, { opacity: 1, yPercent: 0 });
          }
          if (graphCircleStroke) {
            gsap.set(graphCircleStroke, { strokeDashoffset: 0 });
          }
          if (graphCircleFill) {
            gsap.set(graphCircleFill, { fillOpacity: 1, stroke: "white" });
          }
          if (graphCircleText) {
            gsap.set(graphCircleText, { opacity: 1 });
          }
          gsap.set(certifiedImages, {
            opacity: 1,
            scale: 1,
          });
          gsap.set(certifiedInnerImages, {
            scale: 1,
          });
          gsap.set(clippedImageFrames, {
            opacity: 1,
            scale: 1,
            willChange: "auto",
          });
          gsap.set(clippedInnerImages, {
            scale: 1,
          });
          if (cultureImage) {
            gsap.set(cultureImage, { opacity: 1 });
          }
          if (storySection) {
            gsap.set(storySection, { opacity: 1, yPercent: 0 });
          }
          if (iitokodoriImage) {
            gsap.set(iitokodoriImage, { opacity: 1 });
          }
          if (cultureFrames.length > 0) {
            setAkadoriCultureFrame(cultureFrames, cultureFrames[2] || cultureFrames[0]);
          }
          return;
        }

        if (yamagaImage) {
          const yamagaImageTl = gsap.timeline({
            scrollTrigger: {
              trigger: yamagaImage,
              start: "top 50%",
              once: true,
            },
          });
          yamagaImageTl
          .to(yamagaImage, {
            opacity: 1,
            duration: 1.2,
            ease: "none",
          })
          .to(yamagaImage, {
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
          },
          "<"
          );
        }

        if (graphSection && graphCircleStroke && graphCircleFill && graphCircleText) {
          const graphTl = gsap.timeline({
            scrollTrigger: {
              trigger: graphSection,
              start: "top 80%",
              once: true,
            },
          });

          graphTl
            .to(graphSection, {
              opacity: 1,
              yPercent: 0,
              duration: 1.2,
              ease: "power2.out",
            })
            .to(graphCircleStroke, {
              strokeDashoffset: 0,
              duration: 0.8,
              ease: "power2.inOut",
            }, "0.2")
            .to(
              graphCircleFill,
              {
                fillOpacity: 1,
                stroke: "white",
                duration: 0.4,
                ease: "ease.out",
              },
              "-=0.4"
            )
            .to(
              graphCircleText,
              {
                opacity: 1,
                duration: 0.4,
                ease: "ease.out",
              }
            );
        }

        certifiedImages.forEach((certifiedImage) => {
          const innerImage = certifiedImage.querySelector<SVGImageElement>(
            "[data-akadori-certified-image-inner]"
          );
          const certifiedImageTl = gsap.timeline({
            scrollTrigger: {
              trigger: certifiedImage,
              start: "top 70%",
              once: true,
            },
          });

          certifiedImageTl
            .to(
              certifiedImage,
              {
                opacity: 1,
                duration: 0.01,
                ease: "none",
              },
              "0"
            )
            .to(
              certifiedImage,
              {
                scale: 1,
                duration: 1.65,
                ease: AKADORI_CONTENT_CERTIFIED_IMAGE_POP_EASE,
              },
              "0"
            );

          if (innerImage) {
            certifiedImageTl.to(
              innerImage,
              {
                scale: 1,
                duration: 2.3,
                ease: AKADORI_CONTENT_CERTIFIED_IMAGE_POP_EASE,
              },
              "0.1"
            );
          }
        });


        clippedImageFrames.forEach((imageFrame) => {
          if (imageFrame.closest("[data-akadori-content-image-group]")) {
            return;
          }

          const innerImage = imageFrame.querySelector<HTMLElement>(
            "[data-akadori-content-image-inner]"
          );

          if (!innerImage) {
            return;
          }

          const clippedImageTl = gsap.timeline({
            scrollTrigger: {
              trigger: imageFrame,
              start: "top 75%",
              once: true,
            },
          });

          addClippedImageAnimation(clippedImageTl, imageFrame, innerImage, "0");
        });

        clippedImageGroups.forEach((imageGroup) => {
          const groupFrames = Array.from(
            imageGroup.querySelectorAll<HTMLElement>(
              "[data-akadori-content-image]"
            )
          );

          if (groupFrames.length === 0) {
            return;
          }

          const groupTl = gsap.timeline({
            scrollTrigger: {
              trigger: imageGroup,
              start: "top 75%",
              once: true,
            },
          });

          groupFrames.forEach((imageFrame, index) => {
            const innerImage = imageFrame.querySelector<HTMLElement>(
              "[data-akadori-content-image-inner]"
            );

            if (!innerImage) {
              return;
            }

            const position = index * 0.25;

            addClippedImageAnimation(groupTl, imageFrame, innerImage, position);
          });
        });

        if (cultureImage && cultureFrames.length > 0) {
          startAkadoriCultureLoop(cultureFrames);

          gsap.to(cultureImage, {
            opacity: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: cultureImage,
              start: "top 80%",
              once: true,
            },
          });
        }

        if (storySection) {
          gsap.to(storySection, {
            opacity: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: storySection,
              start: "top 80%",
              once: true,
            },
          });
        }

        if (iitokodoriImage) {
          gsap.to(iitokodoriImage, {
            opacity: 1,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: iitokodoriImage,
              start: "top 70%",
              once: true,
            },
          });
        }
      },
    };
  });
};
